import {
  InfrastructureByYearPerMonth,
  InfrastructureMonthStats,
  MONTH_ORDER,
  ReportFilters,
} from '../../../types/types';

/**
 * Only these 7 of the API's 10 categories are shown, per your instruction —
 * distribution_centres, latrines, and other_humanitarian_facility_count
 * are tracked in the type (so nothing is silently dropped from the data)
 * but not displayed. Order here is display order.
 */
export const TRACKED_INFRASTRUCTURE_CATEGORIES: {
  key: keyof InfrastructureMonthStats;
  label: string;
}[] = [
  { key: 'schools', label: 'Schools' },
  { key: 'health_centres', label: 'Health centres' },
  { key: 'water_points', label: 'Water points' },
  { key: 'nutrition_centres', label: 'Nutrition centres' },
  { key: 'protection_counselling', label: 'Protection/ Counselling centres' },
  { key: 'solar_lights', label: 'Solar lights' },
  { key: 'psn_houses', label: 'PSN houses' },
];

export interface InfrastructureCategoryMetric {
  key: string;
  label: string;
  /** Sum from Jan through the cutoff month — same convention as eviction YTD. */
  ytd: number;
  /** The selected month's value directly — null when mode is 'year' (no month chosen). */
  month: number | null;
}

export interface InfrastructureMetrics {
  categories: InfrastructureCategoryMetric[];
}

function cutoffIndex(filters: ReportFilters): number {
  if (filters.month) return MONTH_ORDER.indexOf(filters.month);
  return MONTH_ORDER.length - 1;
}

/**
 * Computes YTD and (when a month is selected) this-month counts for each
 * tracked category. This mirrors evictionMetrics.ts's YTD logic exactly —
 * unlike risk data, infrastructure destruction is a flow (new incidents
 * each month), so summing across months for YTD is meaningful here.
 */
export function computeInfrastructureMetrics(
  data: InfrastructureByYearPerMonth,
  filters: ReportFilters,
): InfrastructureMetrics {
  const yearData = data[String(filters.year)];
  const cutoff = cutoffIndex(filters);
  const includesMonth = filters.mode === 'month' || filters.mode === 'both';

  const categories: InfrastructureCategoryMetric[] = TRACKED_INFRASTRUCTURE_CATEGORIES.map(
    ({ key, label }) => {
      const ytd = MONTH_ORDER.slice(0, cutoff + 1).reduce((sum, m) => {
        return sum + (yearData?.[m]?.[key] ?? 0);
      }, 0);
      const month = includesMonth && filters.month ? yearData?.[filters.month]?.[key] ?? 0 : null;
      return { key, label, ytd, month };
    },
  );

  return { categories };
}